import {
  colorEquals,
  hex,
  hsvToRgb,
  mixColor,
  rgbToHsv,
  toHex,
  withAlpha,
  type Color,
  type Hsv,
} from "../../common/color.js";
import { Container, type ContainerOptions } from "../core/Container.js";
import { Control } from "../core/Control.js";
import type { KeyEventArgs, MouseEventArgs, TextInputArgs } from "../core/events.js";
import { clamp, type Rect } from "../../common/geometry.js";
import { GRADIENT_DOWN, GRADIENT_RIGHT, type RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

/**
 * Which face the drop-down wears.
 *
 * `simple` is a grid of swatches: pick one of these. `advanced` is the
 * saturation/value square with a hue ramp beside it, a hex field and the
 * palette kept as shortcuts — the picker you reach for when the color you
 * want is not on anybody's list.
 */
export type ColorPickerMode = "simple" | "advanced";

export interface ColorPickerOptions extends ContainerOptions {
  color?: Color;
  /** `simple` (a swatch grid) or `advanced` (a spectrum). Defaults to simple. */
  mode?: ColorPickerMode;
  /** The swatches offered. Defaults to a sixteen-color ramp. */
  palette?: readonly Color[];
  /** Columns in the simple drop-down's grid. */
  columns?: number;
  /** Show the color's hex code beside the swatch. */
  showHex?: boolean;
  /**
   * Offer an opacity ramp in advanced mode, and let the picked color carry an
   * alpha below 1. Off by default: most settings want a color, not a glaze.
   */
  allowAlpha?: boolean;
  onChange?: (color: Color) => void;
}

/**
 * The default ramp: eight hues at two brightnesses, plus a grayscale row.
 * Enough to be useful and small enough to read at a glance, which is what a
 * swatch grid is for — a full spectrum picker is a different control.
 */
export const DEFAULT_PALETTE: readonly Color[] = [
  hex("#000000"), hex("#404040"), hex("#808080"), hex("#c0c0c0"),
  hex("#ffffff"), hex("#7f0000"), hex("#ff0000"), hex("#ff7f00"),
  hex("#ffff00"), hex("#00ff00"), hex("#007f00"), hex("#00ffff"),
  hex("#0000ff"), hex("#00007f"), hex("#7f00ff"), hex("#ff00ff"),
];

const SWATCH_SIZE = 20;
const SWATCH_GAP = 3;
const GRID_PADDING = 6;

/**
 * A color swatch that drops either a grid of colors or a full spectrum.
 *
 * The two modes are the same control because they are the same decision at two
 * levels of commitment. A game's dye list or a chart's series colors want
 * `simple`: sixteen swatches, one press, done. A theme editor wants
 * `advanced`, where every color in the cube is reachable and a hex code can
 * be typed in. Switching `mode` at runtime is fine — a "more…" button that
 * flips a simple picker to advanced is a reasonable thing to build.
 *
 * The picker keeps its own HSV alongside the RGBA, because HSV has corners RGB
 * does not: black is one point in RGB but a whole face of the HSV cube, and a
 * picker that recomputed hue from the color would swing the hue ramp back to
 * red the instant the brightness was dragged to zero.
 */
export class ColorPicker extends Container {
  #color: Color;
  /** The working HSV. Authoritative for hue and saturation; see the note above. */
  #hsv: Hsv;
  #palette: Color[];
  #mode: ColorPickerMode;
  #allowAlpha: boolean;
  #popup: Control | null = null;
  /** What to put back when the drop-down is dismissed with Escape. */
  #colorAtOpen: Color | null = null;

  columns: number;
  showHex: boolean;

  constructor(options: ColorPickerOptions = {}) {
    super({ width: 120, height: 28, tabStop: true, cursor: "pointer", ...options });
    this.#allowAlpha = options.allowAlpha ?? false;
    this.#color = options.color ?? DEFAULT_PALETTE[0]!;
    if (!this.#allowAlpha) this.#color = withAlpha(this.#color, 1);
    this.#hsv = rgbToHsv(this.#color);
    this.#palette = [...(options.palette ?? DEFAULT_PALETTE)];
    this.#mode = options.mode ?? "simple";
    this.columns = Math.max(1, options.columns ?? 8);
    this.showHex = options.showHex ?? true;
    if (options.onChange) this.on("change", () => options.onChange!(this.#color));

    this.on("click", () => this.toggleDropDown());
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  get color(): Color {
    return this.#color;
  }
  set color(v: Color) {
    const next = this.#allowAlpha ? v : withAlpha(v, 1);
    if (colorEquals(this.#color, next)) return;
    this.#color = next;
    this.#hsv = adoptHsv(this.#hsv, next);
    this.emit("change");
    this.invalidate();
  }

  /**
   * The same color in the picker's working space.
   *
   * Read it to seed another picker, or to drive a hue-shifted palette. Note
   * that `h` and `s` here are what the picker is holding, which for a gray or
   * a black is the last hue it was actually shown rather than the zero
   * `rgbToHsv` hands back.
   */
  get hsv(): Hsv {
    return { ...this.#hsv };
  }

  /**
   * Set the color from hue, saturation and value, keeping all three even when
   * the resulting RGB throws some of them away.
   */
  setHsv(h: number, s: number, v: number, a: number = this.#hsv.a): void {
    const next: Hsv = {
      h: ((h % 360) + 360) % 360,
      s: clamp(s, 0, 1),
      v: clamp(v, 0, 1),
      a: this.#allowAlpha ? clamp(a, 0, 1) : 1,
    };
    const rgb = hsvToRgb(next.h, next.s, next.v, next.a);
    const moved = !colorEquals(this.#color, rgb);
    this.#hsv = next;
    this.#color = rgb;
    if (moved) this.emit("change");
    this.invalidate();
  }

  get mode(): ColorPickerMode {
    return this.#mode;
  }
  set mode(v: ColorPickerMode) {
    if (v === this.#mode) return;
    this.#mode = v;
    this.closeDropDown();
    this.invalidate();
  }

  /** Whether the advanced drop-down offers an opacity ramp. */
  get allowAlpha(): boolean {
    return this.#allowAlpha;
  }
  set allowAlpha(v: boolean) {
    if (v === this.#allowAlpha) return;
    this.#allowAlpha = v;
    this.closeDropDown();
    // Dropping the channel has to drop the transparency with it, or the
    // control keeps serving a color its own UI no longer admits to.
    if (!v) this.color = withAlpha(this.#color, 1);
    this.invalidate();
  }

  get palette(): readonly Color[] {
    return this.#palette;
  }
  set palette(v: readonly Color[]) {
    this.#palette = [...v];
    this.closeDropDown();
    this.invalidate();
  }

  get droppedDown(): boolean {
    return this.#popup !== null;
  }

  toggleDropDown(): void {
    if (this.droppedDown) this.closeDropDown();
    else this.openDropDown();
  }

  openDropDown(): void {
    const desktop = this.desktop;
    if (!desktop || this.droppedDown || !this.effectivelyEnabled) return;

    const advanced = this.#mode === "advanced";
    const size = advanced
      ? spectrumSize(this.#palette.length, this.#allowAlpha)
      : gridSize(this.#palette.length, this.columns);

    const anchor = this.screenBounds;
    const bounds: Rect = {
      x: Math.min(anchor.x, Math.max(0, desktop.width - size.width)),
      y: anchor.y + anchor.height,
      width: size.width,
      height: size.height,
    };
    // Flip above the swatch when there is no room below, as a combo does.
    if (bounds.y + bounds.height > desktop.height) {
      bounds.y = Math.max(0, anchor.y - bounds.height);
    }

    this.#colorAtOpen = this.#color;
    const popup: Control = advanced
      ? new SpectrumPad(this, bounds)
      : new SwatchGrid(this.#palette, this.columns, this.#color, bounds);

    popup.on("itemactivate", (index) => {
      // The spectrum applies its color live and reports -1; the grid has
      // touched nothing and reports which swatch was taken.
      const picked = index >= 0 ? this.#palette[index] : undefined;
      this.closeDropDown();
      if (picked) this.color = picked;
      this.focus();
    });
    popup.on("blur", () => this.closeDropDown());
    popup.on("closed", () => {
      // Dismissed from outside — a press elsewhere, a theme swap. Whoever did
      // it never went through `closeDropDown`, so catch up here rather than
      // leave `droppedDown` insisting on a popup that is gone.
      if (this.#popup !== popup) return;
      this.#popup = null;
      this.#colorAtOpen = null;
      this.invalidate();
    });
    popup.on("keydown", (e) => {
      if (e.key !== "Escape" || e.handled) return;
      const revert = this.#colorAtOpen;
      this.closeDropDown();
      if (revert) this.color = revert;
      this.focus();
      e.handled = true;
    });

    this.#popup = popup;
    desktop.showPopup(popup);
    popup.focus();
    this.invalidate();
  }

  closeDropDown(): void {
    const popup = this.#popup;
    if (!popup) return;
    const desktop = this.desktop;
    this.#popup = null;
    this.#colorAtOpen = null;
    if (desktop?.popup === popup) desktop.closePopup();
    this.invalidate();
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (e.key === "Enter" || e.key === " " || (e.key === "ArrowDown" && e.altKey) || e.key === "F4") {
      this.toggleDropDown();
      e.handled = true;
      return;
    }
    if (e.key === "Escape" && this.droppedDown) {
      this.closeDropDown();
      e.handled = true;
    }
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const r = this.localRect;
    theme.drawButton(rc, r, { ...this.visualState(), pressed: this.droppedDown });

    const inset = 5;
    const side = Math.max(0, r.height - inset * 2);
    const swatch: Rect = { x: inset, y: inset, width: side, height: side };
    if (this.#color.a < 1) drawCheckerboard(rc, swatch, theme, 3);
    rc.drawRect(swatch, {
      fill: this.#color,
      radius: 2,
      borderWidth: 1,
      borderColor: p.controlBorder,
    });

    if (!this.showHex) return;
    const x = swatch.x + swatch.width + 8;
    rc.drawText(toHex(this.#color), x, r.height / 2, theme.metrics.font, theme.buttonTextColor(this.visualState()), {
      baseline: "middle",
      maxWidth: Math.max(0, r.width - x - inset),
    });
  }
}

/**
 * Keep the hue and saturation the picker is already holding when the incoming
 * color has no opinion of its own. Black has neither; a gray has no hue.
 * Re-deriving both from RGB would snap the ramps to red the moment a color
 * bottomed out, which is the classic way a picker comes to feel broken.
 */
function adoptHsv(previous: Hsv, c: Color): Hsv {
  const measured = rgbToHsv(c);
  return {
    h: measured.s === 0 ? previous.h : measured.h,
    s: measured.v === 0 ? previous.s : measured.s,
    v: measured.v,
    a: measured.a,
  };
}

// --- simple mode ----------------------------------------------------------

function gridSize(count: number, columns: number): { width: number; height: number } {
  const rows = Math.max(1, Math.ceil(count / columns));
  return {
    width: columns * SWATCH_SIZE + (columns - 1) * SWATCH_GAP + GRID_PADDING * 2,
    height: rows * SWATCH_SIZE + (rows - 1) * SWATCH_GAP + GRID_PADDING * 2,
  };
}

/** The simple mode's popup grid. Made by `ColorPicker`; not built directly. */
class SwatchGrid extends Control {
  #colors: readonly Color[];
  #columns: number;
  #highlighted: number;

  constructor(colors: readonly Color[], columns: number, current: Color, bounds: Rect) {
    super({ ...bounds, tabStop: true });
    this.#colors = colors;
    this.#columns = columns;
    const at = colors.findIndex((c) => colorEquals(c, current));
    this.#highlighted = at < 0 ? 0 : at;

    this.on("pointermove", (e) => this.#hover(e));
    this.on("click", (e) => {
      const index = this.indexAt(e.x, e.y);
      if (index >= 0) this.emit("itemactivate", index);
    });
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  indexAt(x: number, y: number): number {
    const stride = SWATCH_SIZE + SWATCH_GAP;
    const column = Math.floor((x - GRID_PADDING) / stride);
    const row = Math.floor((y - GRID_PADDING) / stride);
    if (column < 0 || column >= this.#columns || row < 0) return -1;
    const index = row * this.#columns + column;
    return index < this.#colors.length ? index : -1;
  }

  #hover(e: MouseEventArgs): void {
    const index = this.indexAt(e.x, e.y);
    if (index < 0 || index === this.#highlighted) return;
    this.#highlighted = index;
    this.invalidate();
  }

  #onKeyDown(e: KeyEventArgs): void {
    const move = (delta: number): void => {
      this.#highlighted = clamp(this.#highlighted + delta, 0, this.#colors.length - 1);
      this.invalidate();
      e.handled = true;
    };
    switch (e.key) {
      case "ArrowRight":
        move(1);
        break;
      case "ArrowLeft":
        move(-1);
        break;
      case "ArrowDown":
        move(this.#columns);
        break;
      case "ArrowUp":
        move(-this.#columns);
        break;
      case "Enter":
      case " ":
        this.emit("itemactivate", this.#highlighted);
        e.handled = true;
        break;
      default:
        break;
    }
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    rc.drawRect(this.localRect, {
      fill: p.windowBg,
      radius: theme.metrics.controlCornerRadius,
      borderWidth: 1,
      borderColor: p.controlBorder,
    });

    const stride = SWATCH_SIZE + SWATCH_GAP;
    this.#colors.forEach((color, i) => {
      const box: Rect = {
        x: GRID_PADDING + (i % this.#columns) * stride,
        y: GRID_PADDING + Math.floor(i / this.#columns) * stride,
        width: SWATCH_SIZE,
        height: SWATCH_SIZE,
      };
      rc.drawRect(box, {
        fill: color,
        radius: 2,
        borderWidth: i === this.#highlighted ? 2 : 1,
        borderColor: i === this.#highlighted ? p.accent : p.controlBorder,
      });
    });
  }
}

// --- advanced mode --------------------------------------------------------

const PAD = 8;
const GAP = 8;
const SV_WIDTH = 176;
const SV_HEIGHT = 132;
const STRIP_WIDTH = 14;
const FOOTER_HEIGHT = 24;
const PREVIEW_WIDTH = 34;
const QUICK_SIZE = 14;
const QUICK_GAP = 4;

/** The regions of the advanced drop-down, in its own coordinates. */
interface SpectrumParts {
  /** The saturation (across) by value (down) square. */
  sv: Rect;
  hue: Rect;
  /** Null unless the picker allows alpha. */
  alpha: Rect | null;
  preview: Rect;
  hexField: Rect;
  /** The palette, kept as shortcuts under the spectrum. */
  quick: Rect[];
}

/** Where the palette shortcuts land, given the width they have to fit in. */
function quickCells(count: number, width: number, top: number): Rect[] {
  if (count === 0) return [];
  const stride = QUICK_SIZE + QUICK_GAP;
  const columns = Math.max(1, Math.floor((width - PAD * 2 + QUICK_GAP) / stride));
  return Array.from({ length: count }, (_, i) => ({
    x: PAD + (i % columns) * stride,
    y: top + Math.floor(i / columns) * stride,
    width: QUICK_SIZE,
    height: QUICK_SIZE,
  }));
}

/**
 * The whole advanced layout, and the size it needs. One function so the popup
 * cannot be sized by one rule and painted by another.
 */
function spectrumParts(
  swatches: number,
  withAlpha: boolean,
): SpectrumParts & { width: number; height: number } {
  const sv: Rect = { x: PAD, y: PAD, width: SV_WIDTH, height: SV_HEIGHT };
  const hue: Rect = { x: sv.x + sv.width + GAP, y: PAD, width: STRIP_WIDTH, height: SV_HEIGHT };
  const alpha: Rect | null = withAlpha
    ? { x: hue.x + hue.width + GAP, y: PAD, width: STRIP_WIDTH, height: SV_HEIGHT }
    : null;

  const rightmost = alpha ?? hue;
  const width = rightmost.x + rightmost.width + PAD;

  const footerY = sv.y + sv.height + GAP;
  const preview: Rect = { x: PAD, y: footerY, width: PREVIEW_WIDTH, height: FOOTER_HEIGHT };
  const hexX = preview.x + preview.width + GAP;
  const hexField: Rect = { x: hexX, y: footerY, width: width - PAD - hexX, height: FOOTER_HEIGHT };

  const quick = quickCells(swatches, width, footerY + FOOTER_HEIGHT + GAP);
  const last = quick[quick.length - 1];
  const height = (last ? last.y + last.height : footerY + FOOTER_HEIGHT) + PAD;

  return { sv, hue, alpha, preview, hexField, quick, width, height };
}

function spectrumSize(swatches: number, withAlpha: boolean): { width: number; height: number } {
  const { width, height } = spectrumParts(swatches, withAlpha);
  return { width, height };
}

/** Which piece of the pad the keyboard is currently aimed at. */
type SpectrumArea = "sv" | "hue" | "alpha" | "hex";

/** Hue stops, one per 60 degrees, plus the wrap back round to red. */
const HUE_STOPS: readonly Color[] = [0, 60, 120, 180, 240, 300, 360].map((h) => hsvToRgb(h, 1, 1));

/**
 * The advanced mode's popup. Made by `ColorPicker`; not built directly.
 *
 * It holds no color of its own — every drag writes straight through to the
 * picker. That is what keeps the preview under the pointer and the swatch in
 * the control below it in agreement, and what makes Escape's revert a single
 * assignment rather than a rollback.
 */
class SpectrumPad extends Control {
  #owner: ColorPicker;
  #drag: SpectrumArea | null = null;
  #area: SpectrumArea = "sv";
  /** Non-null while the hex field is being typed into; digits only, no "#". */
  #typing: string | null = null;
  #hoveredQuick = -1;

  constructor(owner: ColorPicker, bounds: Rect) {
    super({ ...bounds, tabStop: true });
    this.#owner = owner;

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", () => this.#endDrag());
    this.on("keydown", (e) => this.#onKeyDown(e));
    this.on("textinput", (e) => this.#onTextInput(e));

    // The pad mirrors the picker, so anything else that moves the color — a
    // handler on the host's `change`, a bound view model — must repaint it.
    // Dropped again when the popup closes: the picker outlives every pad it
    // opens, and a subscription per open would pile them up behind it.
    const repaint = (): void => this.invalidate();
    owner.on("change", repaint);
    this.on("closed", () => owner.off("change", repaint));
  }

  get parts(): SpectrumParts {
    return spectrumParts(this.#owner.palette.length, this.#owner.allowAlpha);
  }

  /**
   * True only while the hex field is live.
   *
   * The pad is a drawing surface the rest of the time, and the keyboard bridge
   * hands printable keys to the hidden field only for a control that says it
   * wants them — so this has to be able to change while the pad keeps focus,
   * which is what `#setTyping` re-syncs.
   */
  override get acceptsTextInput(): boolean {
    return this.#typing !== null;
  }

  /** Open or close the hex field, and tell the keyboard bridge which it was. */
  #setTyping(value: string | null): void {
    if (this.#typing === value) return;
    const wasEditing = this.#typing !== null;
    this.#typing = value;
    // Always from inside a gesture — a press on the field, or Tab onto it —
    // which is the only time a browser will raise a phone's keyboard.
    if (wasEditing !== (value !== null)) this.desktop?.keyboard.sync();
    this.invalidate();
  }

  /** The whole pad is a drag surface, so a pan must not steal it. */
  override handlesDragAt(): boolean {
    return true;
  }

  // --- interaction --------------------------------------------------------

  #onPointerDown(e: MouseEventArgs): void {
    if (e.button !== 0) return;
    const parts = this.parts;

    const quick = parts.quick.findIndex((r) => hits(r, e.x, e.y));
    if (quick >= 0) {
      this.#commitTyping();
      const picked = this.#owner.palette[quick];
      if (picked) this.#owner.color = picked;
      this.invalidate();
      return;
    }
    if (hits(parts.hexField, e.x, e.y)) {
      this.#area = "hex";
      this.#setTyping(toHex(this.#owner.color).slice(1));
      return;
    }

    this.#commitTyping();
    const area = hits(parts.sv, e.x, e.y)
      ? "sv"
      : hits(parts.hue, e.x, e.y)
        ? "hue"
        : parts.alpha && hits(parts.alpha, e.x, e.y)
          ? "alpha"
          : null;
    if (!area) return;

    this.#drag = area;
    this.#area = area;
    this.desktop?.input.setCapture(this);
    this.#applyAt(area, e.x, e.y);
  }

  #onPointerMove(e: MouseEventArgs): void {
    if (this.#drag) {
      this.#applyAt(this.#drag, e.x, e.y);
      return;
    }
    const hovered = this.parts.quick.findIndex((r) => hits(r, e.x, e.y));
    if (hovered === this.#hoveredQuick) return;
    this.#hoveredQuick = hovered;
    this.invalidate();
  }

  #endDrag(): void {
    if (!this.#drag) return;
    this.#drag = null;
    this.desktop?.input.releaseCapture(this);
  }

  /**
   * Turn a point inside one of the three ramps into a color. Clamped rather
   * than rejected, so a drag that runs off the square keeps tracking the edge
   * instead of freezing where it left.
   */
  #applyAt(area: SpectrumArea, x: number, y: number): void {
    const parts = this.parts;
    const { h, s, v, a } = this.#owner.hsv;
    if (area === "sv") {
      this.#owner.setHsv(
        h,
        clamp((x - parts.sv.x) / parts.sv.width, 0, 1),
        1 - clamp((y - parts.sv.y) / parts.sv.height, 0, 1),
        a,
      );
    } else if (area === "hue") {
      this.#owner.setHsv(clamp((y - parts.hue.y) / parts.hue.height, 0, 1) * 360, s, v, a);
    } else if (area === "alpha" && parts.alpha) {
      this.#owner.setHsv(h, s, v, 1 - clamp((y - parts.alpha.y) / parts.alpha.height, 0, 1));
    }
  }

  // --- keyboard -----------------------------------------------------------

  #onKeyDown(e: KeyEventArgs): void {
    if (this.#typing !== null) {
      this.#hexKey(e);
      return;
    }
    const { h, s, v, a } = this.#owner.hsv;
    // Shift is the coarse step here, not the fine one: a hue is 360 units
    // wide, and stepping it one at a time from red to blue is 240 presses.
    const scale = e.shiftKey ? 10 : 1;
    const nudge = (dh: number, ds: number, dv: number, da: number): void => {
      this.#owner.setHsv(h + dh * scale, s + ds * scale, v + dv * scale, a + da * scale);
      e.handled = true;
    };

    switch (e.key) {
      case "Tab":
        this.#area = this.#nextArea(e.shiftKey ? -1 : 1);
        this.invalidate();
        e.handled = true;
        return;
      case "Enter":
      case " ":
        this.emit("itemactivate", -1);
        e.handled = true;
        return;
      case "ArrowLeft":
        if (this.#area === "sv") nudge(0, -0.01, 0, 0);
        else if (this.#area === "hue") nudge(-1, 0, 0, 0);
        else if (this.#area === "alpha") nudge(0, 0, 0, -0.01);
        return;
      case "ArrowRight":
        if (this.#area === "sv") nudge(0, 0.01, 0, 0);
        else if (this.#area === "hue") nudge(1, 0, 0, 0);
        else if (this.#area === "alpha") nudge(0, 0, 0, 0.01);
        return;
      case "ArrowUp":
        if (this.#area === "sv") nudge(0, 0, 0.01, 0);
        else if (this.#area === "hue") nudge(-1, 0, 0, 0);
        else if (this.#area === "alpha") nudge(0, 0, 0, 0.01);
        return;
      case "ArrowDown":
        if (this.#area === "sv") nudge(0, 0, -0.01, 0);
        else if (this.#area === "hue") nudge(1, 0, 0, 0);
        else if (this.#area === "alpha") nudge(0, 0, 0, -0.01);
        return;
      default:
        return;
    }
  }

  /** Tab order within the pad, skipping the alpha ramp when there is none. */
  #nextArea(direction: 1 | -1): SpectrumArea {
    const order: SpectrumArea[] = this.#owner.allowAlpha
      ? ["sv", "hue", "alpha", "hex"]
      : ["sv", "hue", "hex"];
    const at = order.indexOf(this.#area);
    const next = order[(at + direction + order.length) % order.length]!;
    if (next === "hex") this.#setTyping(toHex(this.#owner.color).slice(1));
    return next;
  }

  // --- hex entry ----------------------------------------------------------

  #hexKey(e: KeyEventArgs): void {
    const typed = this.#typing ?? "";
    switch (e.key) {
      case "Backspace":
        this.#typing = typed.slice(0, -1);
        this.invalidate();
        e.handled = true;
        return;
      case "Enter":
        this.#commitTyping();
        this.emit("itemactivate", -1);
        e.handled = true;
        return;
      case "Escape":
        // Abandons the edit, not the drop-down: one Escape per thing open.
        this.#setTyping(null);
        this.#area = "sv";
        e.handled = true;
        return;
      case "Tab":
        this.#commitTyping();
        this.#area = this.#nextArea(e.shiftKey ? -1 : 1);
        this.invalidate();
        e.handled = true;
        return;
      default:
        return;
    }
  }

  #onTextInput(e: TextInputArgs): void {
    if (this.#typing === null) return;
    // Anything that is not a hex digit is dropped, so a pasted "#ff8800" or
    // "0xff8800" lands as the six digits that matter. Eight leaves room for
    // the alpha pair when the picker takes one.
    const digits = e.text.replace(/[^0-9a-f]/gi, "");
    if (digits.length === 0) return;
    this.#typing = (this.#typing + digits).slice(0, this.#owner.allowAlpha ? 8 : 6);
    this.#applyTyping();
    this.invalidate();
    e.handled = true;
  }

  /** Apply the buffer when it happens to be a whole color; stay in the field. */
  #applyTyping(): void {
    const parsed = parseHexDigits(this.#typing, this.#owner.allowAlpha);
    if (parsed) this.#owner.color = parsed;
  }

  #commitTyping(): void {
    if (this.#typing === null) return;
    this.#applyTyping();
    this.#setTyping(null);
  }

  // --- painting -----------------------------------------------------------

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const parts = this.parts;
    const { h, s, v, a } = this.#owner.hsv;

    rc.drawRect(this.localRect, {
      fill: p.windowBg,
      radius: theme.metrics.controlCornerRadius,
      borderWidth: 1,
      borderColor: p.controlBorder,
    });

    this.#paintSquare(rc, theme, parts.sv, h, s, v);
    this.#paintHue(rc, theme, parts.hue, h);
    if (parts.alpha) this.#paintAlpha(rc, theme, parts.alpha, h, s, v, a);
    this.#paintFooter(rc, theme, parts);
    this.#paintQuick(rc, theme, parts);
  }

  #paintSquare(rc: RenderContext, theme: Theme, r: Rect, h: number, s: number, v: number): void {
    // White to the hue across, then black down over the top. Two quads, and
    // the fragment shader interpolates both, so there is no banding to hide
    // and no per-row strip to batch.
    rc.fillGradient(r, WHITE, hsvToRgb(h, 1, 1), GRADIENT_RIGHT, 2);
    rc.fillGradient(r, CLEAR_BLACK, BLACK, GRADIENT_DOWN, 2);
    rc.strokeRect(r, theme.palette.controlBorder, 1, 2);
    if (this.#area === "sv") theme.drawFocusRing(rc, r);
    drawRingHandle(rc, r.x + s * r.width, r.y + (1 - v) * r.height);
  }

  #paintHue(rc: RenderContext, theme: Theme, r: Rect, h: number): void {
    const segment = r.height / 6;
    for (let i = 0; i < 6; i++) {
      // Half a pixel of overlap: each quad antialiases its own edges, and two
      // that merely abut leave a hairline of the background showing between.
      const box: Rect = {
        x: r.x,
        y: r.y + i * segment,
        width: r.width,
        height: segment + (i < 5 ? 0.5 : 0),
      };
      rc.fillGradient(box, HUE_STOPS[i]!, HUE_STOPS[i + 1]!, GRADIENT_DOWN);
    }
    rc.strokeRect(r, theme.palette.controlBorder, 1);
    if (this.#area === "hue") theme.drawFocusRing(rc, r);
    drawStripMarker(rc, r, r.y + (h / 360) * r.height);
  }

  #paintAlpha(
    rc: RenderContext,
    theme: Theme,
    r: Rect,
    h: number,
    s: number,
    v: number,
    a: number,
  ): void {
    const opaque = hsvToRgb(h, s, v, 1);
    drawCheckerboard(rc, r, theme, 4);
    rc.fillGradient(r, opaque, withAlpha(opaque, 0), GRADIENT_DOWN);
    rc.strokeRect(r, theme.palette.controlBorder, 1);
    if (this.#area === "alpha") theme.drawFocusRing(rc, r);
    drawStripMarker(rc, r, r.y + (1 - a) * r.height);
  }

  #paintFooter(rc: RenderContext, theme: Theme, parts: SpectrumParts): void {
    const p = theme.palette;
    const color = this.#owner.color;

    if (color.a < 1) drawCheckerboard(rc, parts.preview, theme, 4);
    rc.drawRect(parts.preview, {
      fill: color,
      radius: 2,
      borderWidth: 1,
      borderColor: p.controlBorder,
    });

    const editing = this.#typing !== null;
    theme.drawTextBox(rc, parts.hexField, {
      hovered: false,
      pressed: false,
      focused: editing,
      disabled: false,
    });
    // A caret only while the field is where the keys are going; the buffer is
    // also shown when Tab has merely passed through it.
    const caret = editing && this.#area === "hex" ? "_" : "";
    const text = editing ? `#${this.#typing}${caret}` : toHex(color);
    rc.drawTextInRect(text, parts.hexField, theme.metrics.monoFont, p.text, 6);
  }

  #paintQuick(rc: RenderContext, theme: Theme, parts: SpectrumParts): void {
    const p = theme.palette;
    const current = this.#owner.color;
    parts.quick.forEach((box, i) => {
      const color = this.#owner.palette[i]!;
      const marked = i === this.#hoveredQuick || colorEquals(color, current);
      rc.drawRect(box, {
        fill: color,
        radius: 2,
        borderWidth: marked ? 2 : 1,
        borderColor: marked ? p.accent : p.controlBorder,
      });
    });
  }
}

// --- shared painting ------------------------------------------------------

const WHITE: Color = { r: 1, g: 1, b: 1, a: 1 };
const BLACK: Color = { r: 0, g: 0, b: 0, a: 1 };
/** Black with nothing in it — the clear end of the value ramp's shade. */
const CLEAR_BLACK: Color = { r: 0, g: 0, b: 0, a: 0 };

function hits(r: Rect, x: number, y: number): boolean {
  return x >= r.x && x < r.x + r.width && y >= r.y && y < r.y + r.height;
}

/**
 * "ff8800" or "ff8800aa" as a color, or null while it is still half-typed.
 *
 * The four- and eight-digit forms are only whole colors where the picker
 * takes an alpha; otherwise four digits is "3a7b", four keystrokes into a
 * six-digit code, and reading it as #rgba would flash a color on screen that
 * nobody was typing.
 */
function parseHexDigits(digits: string | null, allowAlpha: boolean): Color | null {
  if (digits === null) return null;
  const complete =
    digits.length === 3 ||
    digits.length === 6 ||
    (allowAlpha && (digits.length === 4 || digits.length === 8));
  if (!complete) return null;
  try {
    return hex(digits);
  } catch {
    return null;
  }
}

/**
 * The transparency backdrop, so a half-opaque swatch reads as half-opaque.
 *
 * The two tones are mixed from the surface towards the ink rather than taken
 * from the palette, so the squares stay distinguishable in a dark theme as
 * well as a light one — a fixed pair of grays disappears into one of the two.
 */
function drawCheckerboard(rc: RenderContext, r: Rect, theme: Theme, size: number): void {
  const p = theme.palette;
  const light = mixColor(p.windowBg, p.text, 0.06);
  const dark = mixColor(p.windowBg, p.text, 0.2);
  rc.clipped(r, () => {
    rc.fillRect(r, light);
    for (let row = 0; row * size < r.height; row++) {
      for (let col = row % 2; col * size < r.width; col += 2) {
        const x = r.x + col * size;
        const y = r.y + row * size;
        rc.fillRect(
          {
            x,
            y,
            width: Math.min(size, r.x + r.width - x),
            height: Math.min(size, r.y + r.height - y),
          },
          dark,
        );
      }
    }
  });
}

/**
 * The saturation/value handle: a ring rather than a dot, because it sits on
 * top of the color it is choosing and a filled marker would hide it. Two
 * rings, dark outside and light inside, so it survives both ends of the ramp.
 */
function drawRingHandle(rc: RenderContext, cx: number, cy: number): void {
  rc.drawRect(
    { x: cx - 6, y: cy - 6, width: 12, height: 12 },
    { radius: 6, borderWidth: 1, borderColor: BLACK },
  );
  rc.drawRect(
    { x: cx - 5, y: cy - 5, width: 10, height: 10 },
    { radius: 5, borderWidth: 2, borderColor: WHITE },
  );
}

/** A ramp's marker: a bar straddling the strip, for the same contrast reason. */
function drawStripMarker(rc: RenderContext, strip: Rect, y: number): void {
  const bar: Rect = { x: strip.x - 2, y: y - 3, width: strip.width + 4, height: 6 };
  rc.drawRect(bar, { radius: 3, borderWidth: 1, borderColor: BLACK });
  rc.drawRect(
    { x: bar.x + 1, y: bar.y + 1, width: bar.width - 2, height: bar.height - 2 },
    { radius: 2, borderWidth: 1, borderColor: WHITE },
  );
}
